import React from "react";
import {
  FormErrorMessage,
  FormControl,
  FormLabel,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
import illustration2 from "assets/img/auth/auth1.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { loginThunk } from "store/slices/auth";
import { useDispatch } from "react-redux";
import { API_SUCCESS_RESPONSE_TAG } from "lib/constants";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email.")
    .required("Email is required."),
  // password: yup
  //   .string()
  //   .trim()
  // .min(8, 'Password must contain atleast 8 characters')
  // .matches(
  //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
  //   'Password must contain atleast 8 characters, one uppercase, one lowercase, one number and one special case character'
  // )
  // .required('Password is required field')
  password: yup.string().required("Password is required"),
});

function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "#898FAA";
  const inputColor = "#75582B";
  const forgotColor = "#FF1818";
  const brandStars = useColorModeValue("brand.1050", "brand.400");
  const bgButtonGradient = "linear-gradient(180deg, #9BA5D2 0%, #3A50AC 100%)";

  const [show, setShow] = React.useState(false);

  const dispatch = useDispatch();
  const handleClick = () => setShow(!show);
  const history = useHistory();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    const res = await dispatch(
      loginThunk({ email: data?.email, password: data?.password })
    );
    if (res.payload === API_SUCCESS_RESPONSE_TAG) {
      history.push("/auth/welcome");
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration2} image={illustration2}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Sign In
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          {/* <Flex align="center" mb="25px">
            <HSeparator />
          </Flex> */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={errors.email}>
              <Box mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Email<Text color={brandStars}>*</Text>
                </FormLabel>
                <Input
                  {...register("email")}
                  id="email"
                  variant="auth"
                  fontSize="sm"
                  ms={{ base: "0px", md: "0px" }}
                  type="email"
                  placeholder="example@email.com"
                  fontWeight="500"
                  size="lg"
                  _placeholder={{ color: "#898FAA" }}
                  borderColor="#172C87"
                  borderRadius="16px"
                  border="2.5px solid"
                  color="#172C87"
                />
                <FormErrorMessage>
                  {errors.email && errors.email.message}
                </FormErrorMessage>
              </Box>

              <Box mb="24px">
                <FormControl isInvalid={errors.password}>
                  <FormLabel
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    display="flex"
                  >
                    Password<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      {...register("password")}
                      fontSize="sm"
                      placeholder="Min. 8 characters"
                      size="lg"
                      type={show ? "text" : "password"}
                      variant="auth"
                      _placeholder={{ color: "#898FAA" }}
                      borderColor="#172C87"
                      borderRadius="16px"
                      border="2.5px solid"
                      color="#172C87"
                    />
                    <InputRightElement
                      display="flex"
                      alignItems="center"
                      mt="4px"
                    >
                      <Icon
                        color={inputColor}
                        _hover={{ cursor: "pointer" }}
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {errors.password && errors.password.message}
                  </FormErrorMessage>
                </FormControl>
              </Box>
              <Flex
                justifyContent="space-between"
                align="center"
                mb="24px"
                w="100%"
                gap="12px"
                flexWrap="nowrap"
              >
                <Flex align="center" minW="0">
                  <Checkbox
                    id="remember-login"
                    colorScheme="brandScheme"
                    flexShrink={0}
                  />
                  <FormLabel
                    htmlFor="remember-login"
                    mb="0"
                    ms="10px"
                    fontWeight="normal"
                    color={textColor}
                    fontSize="sm"
                    whiteSpace="nowrap"
                  >
                    Keep me logged in
                  </FormLabel>
                </Flex>
                <Text
                  as="a"
                  href="/auth/forgot-password"
                  color={forgotColor}
                  fontSize="sm"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  Forgot password?
                </Text>
              </Flex>
              <Button
                isLoading={isSubmitting}
                type="submit"
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                mb="24px"
                bg={bgButtonGradient}
                _hover={{ opacity: 0.8 }}
              >
                <Text
                  fontFamily={"'DM Sans', sans-serif"}
                  fontWeight="700"
                  fontSize="32px"
                  lineHeight="28px"
                  textAlign="center"
                >
                  Sign In
                </Text>
              </Button>
            </FormControl>
          </form>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
